<template>
  <div class="lenz-law-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$Φ_ind ∝ -dΦ/dt$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage" @mousemove="onContainerMove" @mouseup="stopDrag" @mouseleave="stopDrag">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>磁通量变化率 ΔΦ/Δt</label>
            <span class="val">{{ Math.abs(velocity).toFixed(2) }} <small>Wb/s</small></span>
          </div>
          <div class="hud-item orange">
            <label>线圈感应极性</label>
            <span class="val" :style="{color: currentPolarityColor}">{{ currentPolarity }}</span>
          </div>
        </div>

        <svg width="100%" height="280" viewBox="0 0 400 280" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-ind"><feGaussianBlur stdDeviation="4" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <linearGradient id="magnet-n" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#f43f5e" /><stop offset="100%" stop-color="#991b1b" /></linearGradient>
             <linearGradient id="magnet-s" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#1e293b" /><stop offset="100%" stop-color="#0f172a" /></linearGradient>
          </defs>

          <!-- 参照线圈 (Coil) -->
          <g transform="translate(200, 200)">
            <!-- 后半圆 -->
            <path d="M -70,0 A 70,22 0 0 1 70,0" fill="none" stroke="#1e293b" stroke-width="3" stroke-dasharray="4,4" />
            
            <!-- 感应电流光环 (只有运动时显现) -->
            <circle v-if="Math.abs(velocity) > 0.2" r="75" fill="none" stroke="#10b981" 
                    :stroke-width="Math.abs(velocity) * 4" opacity="0.4" filter="url(#glow-ind)" />

            <!-- 前半圆 -->
            <path d="M -70,0 A 70,22 0 0 0 70,0" fill="none" stroke="#94a3b8" stroke-width="5" stroke-linecap="round" />
            
            <!-- 感应电流方向箭头 -->
            <g v-if="Math.abs(velocity) > 0.2">
               <path :d="velocity > 0 ? 'M 50,12 L 70,5' : 'M -50,12 L -70,5'" stroke="#10b981" stroke-width="3" marker-end="url(#arr-ind)" />
               <text x="0" y="45" text-anchor="middle" fill="#10b981" font-size="10" font-weight="900">I_ind (感应电流)</text>
            </g>
          </g>

          <!-- 磁铁 (Magnet) -->
          <g :transform="`translate(175, ${magnetY})`" style="cursor: grab" @mousedown="startDrag">
            <rect width="50" height="40" fill="url(#magnet-n)" rx="2" />
            <text x="25" y="25" text-anchor="middle" fill="#fff" font-size="16" font-weight="900">N</text>
            <rect y="40" width="50" height="40" fill="url(#magnet-s)" rx="2" />
            <text x="25" y="65" text-anchor="middle" fill="#fff" font-size="16" font-weight="900">S</text>
            
             <!-- 趋近时的相互作用力箭头 (F) -->
             <g v-if="Math.abs(velocity) > 0.5" transform="translate(25, 90)">
                <line x1="0" y1="0" x2="0" :y2="velocity > 0 ? -40 : 40" stroke="#fbbf24" stroke-width="4" marker-end="url(#arr-f)" />
                <text x="15" :y="velocity > 0 ? -20 : 30" fill="#fbbf24" font-size="11" font-weight="900">{{ velocity > 0 ? '排斥力 F' : '引力 F' }}</text>
             </g>
          </g>

          <defs>
             <marker id="arr-ind" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#10b981" /></marker>
             <marker id="arr-f" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#fbbf24" /></marker>
          </defs>
        </svg>

        <div class="interaction-hint">垂直拖动磁铁 ↕ 靠近或远离线圈</div>
      </div>

      <div class="compact-controls">
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>“来拒去留”。当你试图将磁铁<strong>推向</strong>线圈时，线圈会产生相同极性试图<strong>排斥</strong>它；当你试图<strong>拔出</strong>磁铁时，线圈又产生相反极性试图<strong>挽留</strong>它。这种“反抗”外界变化的本性，正是能量守恒律在电磁世界中的必然体现。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'LenzLawViz',
  data() {
    return {
      magnetY: 40,
      velocity: 0,
      dragging: false,
      lastY: 40
    }
  },
  computed: {
    currentPolarity() {
       if (this.velocity > 0.2) return "上方为 N (阻碍趋近)";
       if (this.velocity < -0.2) return "上方为 S (阻碍远去)";
       return "无磁场响应";
    },
    currentPolarityColor() {
       return Math.abs(this.velocity) > 0.2 ? '#10b981' : '#64748b';
    }
  },
  methods: {
    startDrag(e) {
       this.dragging = true;
       this.lastY = e.clientY;
    },
    onContainerMove(e) {
       if (!this.dragging) return;
       const dy = e.clientY - this.lastY;
       this.magnetY = Math.max(10, Math.min(130, this.magnetY + dy));
       this.velocity = dy; // Direct velocity proxy
       this.lastY = e.clientY;
       
       clearTimeout(this.vTimer);
       this.vTimer = setTimeout(() => { this.velocity = 0; }, 100);
    },
    stopDrag() { this.dragging = false; this.velocity = 0; }
  }
}
</script>

<style scoped lang="scss">
.lenz-law-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 280px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #10b981; } label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } } }
.interaction-hint { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; pointer-events: none; }
.discovery-card-compact { background: #ecfdf5; padding: 10px 14px; border-radius: 10px; border: 1px solid #d1fae5; display: flex; align-items: center; gap: 10px; i { color: #10b981; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #065f46; line-height: 1.5; strong { color: #064e3b; } } }
</style>
